import { useEffect, useMemo, useState } from "react";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { UserAvatarItem } from "src/components/ui/TGUserAvatarsList";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import {
  NotifyMyEmployeesType,
  useSelectEmployeesContext,
} from "src/pages/DashboardPage/acadia/notificationPage/SelectEmployees";

import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

const tableHeaderContent = {
  user: {
    title: "ФИО",
    sort: true,
    style: { width: "25%" },
  },
  location: {
    title: "Локация",
    sort: false,
    style: { width: "19%" },
  },
  department: {
    title: "Отдел",
    sort: false,
    style: { width: "19%" },
  },
  position: {
    title: "Должность",
    sort: false,
    style: { width: "19%" },
  },
  workstation: {
    title: "Рабочее место",
    sort: false,
    style: { width: "19%" },
  },
};

const UsersTableWithFilters = ({ items }: { items: NotifyMyEmployeesType[] }) => {
  const limit = 10;

  const status = "SUCCESS";

  if (status !== "SUCCESS") return <Loading />;

  return <Table tableName="Table with filters" header={tableHeaderContent} body={items} limit={limit} />;
};

export default UsersTableWithFilters;

export type CodeType = { code: string; name: string };

export type TableBodyContentItem = {
  id: number;
  user: {
    avatar: string | null;
    name: string;
    email: string;
    id: number;
    personalNumber: string;
    shortName: string;
  };
  location: string;
  department: string;
  position: string;
  workstation: string;
};

function Table({
  header,
  body,
  tableName,
  limit,
}: {
  header: typeof tableHeaderContent;
  body: TableBodyContentItem[];
  limit: number;
  tableName: string;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (sortedField === "user") {
        return sortDirection === "asc"
          ? a[sortedField].name.localeCompare(b[sortedField].name)
          : b[sortedField].name.localeCompare(a[sortedField].name);
      }

      const valueA = a[sortedField as keyof TableBodyContentItem];
      const valueB = b[sortedField as keyof TableBodyContentItem];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as TableBodyContentItem[];

  return (
    <TableList
      head={header}
      body={sortedTableBodyContent}
      sort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
    />
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
}: {
  head: typeof tableHeaderContent;
  body: NotifyMyEmployeesType[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
}) {
  const { items, addItems, addItem, removeItem, removeItems } = useSelectEmployeesContext();

  const [checkedAll, setCheckedAll] = useState(false);

  function checkAll(state: boolean) {
    if (state) {
      addItems(body);
    } else {
      removeItems(body);
    }

    setCheckedAll(state);
  }

  function handleCheckItem(item: NotifyMyEmployeesType, isCheck: boolean) {
    if (isCheck) {
      addItem(item);
    } else {
      removeItem(item.id);
    }
  }

  const tableFields = useMemo(() => {
    return Object.keys(head) as (keyof typeof tableHeaderContent)[];
  }, [head]);

  useEffect(() => {
    const isEveryBody = body.every((item) => items.has(item.id));

    if (body.length > 0 && isEveryBody) {
      setCheckedAll(true);
    } else {
      setCheckedAll(false);
    }
  }, [body, items, items.size]);

  const sortedListBySelected = useMemo(
    () => body.sort((a, b) => (items.has(a.id) > items.has(b.id) ? -1 : items.has(a.id) < items.has(b.id) ? 1 : 0)),
    [body, items]
  );

  return (
    <div className="w-auto bg-white rounded-lg table-custom shadow-custom">
      <FlexRow className="w-auto min-w-[825px]">
        <FlexCol className="relative grow">
          <FlexRow id="sticky-header" className="z-10 gap-x-4 px-6 pt-5 pb-5 bg-white border-b sticky-custom">
            <FlexCol
              className="break-words cursor-pointer"
              onClick={() => checkAll(!checkedAll)}
              style={{ width: "3%" }}
            >
              <input type="checkbox" checked={checkedAll} onChange={(e) => checkAll(!checkedAll)} className="sr-only" />
              <div
                className={twMerge(
                  checkedAll ? "border-darkblue" : "border-[#DFE4EA]",
                  "flex items-center justify-center w-5 h-5 mr-2 border rounded"
                )}
              >
                <span className={twMerge(checkedAll ? "opacity-100" : "opacity-0")}>
                  <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                      fill={"#012169"}
                      stroke={"#012169"}
                      strokeWidth="0.4"
                    />
                  </svg>
                </span>
              </div>
            </FlexCol>
            {tableFields.map((fieldKey) => {
              return (
                <TableHeaderColumn
                  key={fieldKey}
                  tableHeaderContent={head[fieldKey]}
                  sortDirection={sortedField === fieldKey ? sortDirection : "default"}
                  onClick={() => sort(fieldKey)}
                />
              );
            })}
          </FlexRow>
          <FlexRow className="px-6 pb-5 overflow-auto scrollbar scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB]">
            <FlexCol className="divide-y grow">
              {Array.isArray(sortedListBySelected) && sortedListBySelected.length > 0 ? (
                sortedListBySelected.map((item) => {
                  return (
                    <TableItem
                      key={item.id}
                      item={item}
                      isChecked={items.has(item.id)}
                      isCheckedAll={checkedAll}
                      handleCheckItem={handleCheckItem}
                    />
                  );
                })
              ) : (
                <EmptyList title="Сотрудники не найдены" />
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function TableItem({
  item,
  disabled = false,
  isChecked,
  isCheckedAll,
  handleCheckItem,
}: {
  item: NotifyMyEmployeesType;
  disabled?: boolean;
  isChecked: boolean;
  isCheckedAll: boolean;
  handleCheckItem: (item: NotifyMyEmployeesType, isCheck: boolean) => void;
}) {
  const [checked, setChecked] = useState(isChecked);

  function handleChange(input: boolean) {
    setChecked(input);
    handleCheckItem(item, input);
  }

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  return (
    <FlexRow
      className="py-4 gap-x-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer -mx-2 px-2"
      key={item.id}
      onClick={() => handleChange(!checked)}
    >
      <FlexCol className="break-words" style={{ width: "3%" }}>
        <input
          id={`document-${item.id}`}
          name={`document-${item.id}`}
          type="checkbox"
          checked={checked}
          onChange={() => handleChange(!checked)}
          className="sr-only"
        />
        <div
          className={twMerge(
            checked ? "border-darkblue" : "border-[#DFE4EA]",
            "flex items-center justify-center w-5 h-5 mr-2 border rounded"
          )}
        >
          <span className={twMerge(checked ? "opacity-100" : "opacity-0")}>
            <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                fill={twMerge(disabled ? "#9CA3AF" : "#012169")}
                stroke={twMerge(disabled ? "#9CA3AF" : "#012169")}
                strokeWidth="0.4"
              />
            </svg>
          </span>
        </div>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "25%" }}>
        <FlexRow className="gap-x-[5px] items-center">
          <UserAvatarItem item={item.user} single={true} />
        </FlexRow>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "19%" }}>
        <span>{item.location}</span>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "19%" }}>
        <div>{item.department}</div>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "19%" }}>
        <span>{item.position}</span>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "19%" }}>
        <span>{item.workstation}</span>
      </FlexCol>
    </FlexRow>
  );
}

function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
